import { computed, toValue, type MaybeRefOrGetter } from "vue";
import type { SiteResourceDetailed, SitePullResource } from "@carnet/api/backoffice";

type SiteDetail = SiteResourceDetailed | SitePullResource;

// Seul le bassin « Eau douce » porte une altitude significative (lacs, carrières de montagne).
const FRESH_WATER_ZONE_CODE = "eau-douce";

export interface SiteAttributeRow {
  key: string;
  label: string;
  value: string;
}

export interface SiteTextField {
  key: string;
  label: string;
  value: string;
  link: string;
  linkLabel: string;
}

export function useAppSiteDetailView(site: MaybeRefOrGetter<SiteDetail | null | undefined>) {
  const { t } = useI18n();
  const detail = computed(() => toValue(site) ?? undefined);

  const attributes = computed<SiteAttributeRow[]>(() => {
    const d = detail.value;
    if (!d) return [];
    const rows: SiteAttributeRow[] = [];
    if (d.condition?.label) rows.push({ key: "condition", label: t("site.condition"), value: d.condition.label });
    if (d.depthCategory?.label) rows.push({ key: "depth", label: t("site.depth"), value: d.depthCategory.label });
    if (d.maxDepth) rows.push({ key: "maxDepth", label: t("site.maxDepth"), value: `${d.maxDepth} m` });
    if (d.situation?.label) rows.push({ key: "situation", label: t("site.situation"), value: d.situation.label });
    if (d.environment?.label) rows.push({ key: "environment", label: t("site.environment"), value: d.environment.label });
    if (d.altitude != null && d.zone?.code === FRESH_WATER_ZONE_CODE) {
      rows.push({ key: "altitude", label: t("site.altitude"), value: `${d.altitude} m` });
    }
    return rows;
  });

  const habitats = computed(() => detail.value?.habitats ?? []);

  const localFeatures = computed(() => detail.value?.localFeatures ?? []);

  // Champs texte libres, dans l'ordre de la maquette web ; `regulated_access` porte un lien externe
  // optionnel. La description / l'URL externe sont volontairement absentes (bloc d'introduction).
  const textFields = computed<SiteTextField[]>(() => {
    const d = detail.value;
    if (!d) return [];
    return [
      { key: "services", label: t("site.services"), value: d.services, link: "", linkLabel: "" },
      { key: "security", label: t("site.security"), value: d.security, link: "", linkLabel: "" },
      { key: "interests", label: t("site.interests"), value: d.interests, link: "", linkLabel: "" },
      { key: "regulated_access", label: t("site.regulated_access"), value: d.regulated_access, link: d.regulated_access_link, linkLabel: t("site.regulated_access_link") },
      { key: "facilities", label: t("site.facilities"), value: d.facilities, link: "", linkLabel: "" },
      { key: "more_informations", label: t("site.more_informations"), value: d.more_informations, link: "", linkLabel: "" },
    ].filter((field) => field.value?.trim() || field.link);
  });

  const mapCenter = computed<[number, number] | undefined>(() => {
    const d = detail.value;
    if (!d?.latitude || !d?.longitude) return undefined;
    return [d.longitude, d.latitude];
  });

  const mapMarkers = computed(() => (mapCenter.value ? [{ coordinates: mapCenter.value }] : []));

  const directionsUrl = computed(() => {
    const d = detail.value;
    if (!d?.latitude || !d?.longitude) return undefined;
    return `https://www.google.com/maps/dir/?api=1&destination=${d.latitude},${d.longitude}`;
  });

  const siteTag = computed(() => [detail.value?.environment?.label, detail.value?.depthCategory?.label].filter(Boolean).join(" – "));

  const externalUrl = computed(() => safeExternalHttpUrl(detail.value?.external_url));

  return { detail, attributes, habitats, localFeatures, textFields, mapCenter, mapMarkers, directionsUrl, siteTag, externalUrl };
}
